import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { CreditCard, MessageSquare, LogOut, Loader2, ShieldCheck, ArrowRight, FileSignature } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getMissingWaivers } from "@/lib/waiverStatus";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Hard gate for ACTIVE MEMBERS only. When a parent's status is "member" but
 * their Stripe customer has no payment method on file, this full-screen
 * overlay blocks every parent surface until they either:
 *   1. Add a card (redirects to Stripe Checkout in setup mode)
 *   2. Message their coach (navigates to /dashboard/messages — the only
 *      route exempted from the gate so they can flag card-save issues)
 *   3. Sign out
 *
 * Non-dismissable. Re-checks /billing/me after returning with ?card_added=1.
 * Hidden on /dashboard/messages so the escape hatch actually works.
 */
export default function MemberCardGate() {
    const { user, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [state, setState] = useState({ loading: true, hasCard: true });
    const [sending, setSending] = useState(false);

    const isMember = user?.role === "client" && user?.status === "member" && !user?.athlete_mode_for;
    const onMessages = location.pathname.startsWith("/dashboard/messages");
    // Sachin 2026-06: the waiver must ALWAYS be reachable. Exempt the waiver
    // signing page from the card gate (same escape-hatch pattern as messages)
    // and surface a "Sign your waiver" action inside the gate when one is
    // pending, so a card-less member isn't trapped away from it.
    const onWaiver = location.pathname.startsWith("/welcome/waiver");
    const waiverPending = getMissingWaivers(user).length > 0;

    const check = useCallback(async () => {
        if (!isMember) {
            setState({ loading: false, hasCard: true });
            return true;
        }
        // 2026-08 (Sachin: "someone wasn't prompted on mobile"): spotty phone
        // connections can drop the first check — retry twice before failing
        // open, so one blip no longer skips the prompt for the whole session.
        for (let i = 0; i < 3; i++) {
            try {
                const r = await axios.get(`${API}/billing/me`, { withCredentials: true });
                const has = !!r.data?.has_payment_method;
                setState({ loading: false, hasCard: has });
                return has;
            } catch {
                if (i < 2) await new Promise((res) => setTimeout(res, 1500));
            }
        }
        // Persistent failure — fail open (don't lock a member out of their
        // dashboard if our own API is the thing that broke).
        setState({ loading: false, hasCard: true });
        return true;
    }, [isMember]);

    useEffect(() => { check(); }, [check]);

    // Re-check after returning from Stripe Checkout success.
    //
    // Stripe attaches the PM to the customer synchronously when Checkout
    // completes, BUT in practice we've seen 1-15s of lag where /billing/me's
    // PaymentMethod.list call still returns empty (Stripe's read-after-write
    // consistency on PaymentMethods is not strict). Without retry the gate
    // re-flashes after the Stripe redirect even though the card DID save,
    // which is what's been happening to new parents.
    //
    // Strategy: poll every 1.5s up to 8 attempts (~12s). If we ever see a
    // card, stop polling and clear the gate. After the budget runs out we
    // accept reality — the gate stays up and the parent can re-click "Add
    // card" or message their coach. Bug fix 2026-02-17.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (!params.has("card_added")) return;
        let cancelled = false;
        let attempts = 0;
        const maxAttempts = 8;
        const poll = async () => {
            if (cancelled) return;
            const ok = await check();
            attempts += 1;
            if (ok || attempts >= maxAttempts || cancelled) {
                // Strip the query param so a refresh doesn't re-trigger the loop.
                navigate(location.pathname, { replace: true });
                return;
            }
            setTimeout(poll, 1500);
        };
        // Kick off immediately so the modal closes the instant Stripe is ready.
        poll();
        return () => { cancelled = true; };
    }, [location.search, location.pathname, check, navigate]);

    const onAddCard = async () => {
        setSending(true);
        try {
            const r = await axios.post(
                `${API}/billing/setup-card`,
                { origin_url: window.location.origin },
                { withCredentials: true },
            );
            if (r.data?.url) {
                window.location.assign(r.data.url);
            } else {
                toast.error("Couldn't open the card setup page.");
            }
        } catch (e) {
            toast.error("Couldn't open card setup", {
                description: e?.response?.data?.detail || "Please try again or message your coach.",
            });
        } finally {
            setSending(false);
        }
    };

    const onMessageCoach = () => navigate("/dashboard/messages");

    const onLogout = async () => {
        try { await logout(); } finally { navigate("/", { replace: true }); }
    };

    // Don't render anything until we've checked. Avoids a flash of the modal
    // on initial paint for members who DO have a card.
    if (state.loading) return null;
    if (!isMember) return null;
    if (state.hasCard) return null;
    // Allow members to use the messaging escape hatch — they need somewhere
    // to flag "my card won't save" to a human.
    if (onMessages) return null;
    // Never block the waiver page — it must always be reachable.
    if (onWaiver) return null;

    return (
        <div
            data-testid="member-card-gate"
            role="dialog"
            aria-modal="true"
            aria-labelledby="member-card-gate-title"
            className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
        >
            <div className="w-full max-w-lg border border-brand-red/40 bg-gradient-to-br from-brand-red/15 via-zinc-950 to-black rounded-xl shadow-2xl">
                <div className="px-6 py-5 border-b border-brand-red/30">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red font-semibold">
                        Action required
                    </div>
                    <h2
                        id="member-card-gate-title"
                        className="font-display text-2xl md:text-3xl tracking-wider uppercase text-white mt-1"
                    >
                        Add a payment method to continue
                    </h2>
                </div>
                <div className="px-6 py-5 space-y-4">
                    <p className="text-sm text-zinc-200 leading-relaxed">
                        We don&apos;t have a card on file for your account yet. Your membership runs on
                        auto-billing, so we need a saved card before you can keep using the dashboard.
                    </p>
                    <div className="flex items-start gap-2 text-xs text-zinc-300">
                        <ShieldCheck className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                        <span>
                            Secured by Stripe. We never see your card number — Stripe stores it directly
                            and we only ever get a token that lets us charge it.
                        </span>
                    </div>
                    <button
                        type="button"
                        data-testid="member-card-gate-add-card"
                        onClick={onAddCard}
                        disabled={sending}
                        className="w-full inline-flex items-center justify-center gap-2 bg-brand-red hover:bg-brand-red/90 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold tracking-wider uppercase text-sm py-3 rounded-lg transition-colors"
                    >
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                        {sending ? "Opening Stripe…" : "Add payment method"}
                        {!sending && <ArrowRight className="w-4 h-4" />}
                    </button>
                </div>
                {waiverPending && (
                    <div className="px-6 pb-5">
                        <button
                            type="button"
                            data-testid="member-card-gate-sign-waiver"
                            onClick={() => navigate("/welcome/waiver")}
                            className="w-full inline-flex items-center justify-center gap-2 bg-amber-500/15 hover:bg-amber-500/25 text-amber-100 border border-amber-500/40 text-sm py-2.5 rounded-lg transition-colors"
                        >
                            <FileSignature className="w-4 h-4 text-amber-300" /> Sign your waiver first
                        </button>
                    </div>
                )}
                <div className="px-6 py-4 border-t border-zinc-800 bg-black/40 rounded-b-xl">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2">
                        Card won&apos;t save?
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                        <button
                            type="button"
                            data-testid="member-card-gate-message-coach"
                            onClick={onMessageCoach}
                            className="flex-1 inline-flex items-center justify-center gap-2 bg-zinc-900 hover:bg-zinc-800 text-white text-sm py-2.5 rounded-lg border border-zinc-700 transition-colors"
                        >
                            <MessageSquare className="w-4 h-4" /> Message your coach
                        </button>
                        <button
                            type="button"
                            data-testid="member-card-gate-logout"
                            onClick={onLogout}
                            className="inline-flex items-center justify-center gap-2 bg-transparent hover:bg-zinc-900 text-zinc-300 hover:text-white text-sm py-2.5 px-4 rounded-lg border border-zinc-800 transition-colors"
                        >
                            <LogOut className="w-4 h-4" /> Sign out
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
